/**
 * Static legend for the anomaly score → priority mapping. Operators reference
 * this when triaging the unreviewed queue.
 *
 * Priority colors are intentional traffic-light data viz (P0 = urgent red →
 * P4 = neutral) — exempt from the MOPS "single accent color" rule that
 * applies to chrome. Stops are tuned for the dark-only theme.
 */
const AnomalyScoreRanges = () => {
  return (
    <section className="border-border-primary flex flex-col gap-3 border p-4">
      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Anomaly score ranges</h2>
      <div className="grid grid-cols-3 gap-2">
        <PriorityRow color="red" label="P0: 10" description="Urgent — notify immediately. Extremely prominent." />
        <PriorityRow color="yellow" label="P1: ≥9" description="High priority — notify soon. Very prominent." />
        <PriorityRow color="blue" label="P2: ≥8" description="Medium — possible weekly summary. Prominent." />
        <PriorityRow color="green" label="P3: ≥6" description="Low — no notification, deep-link FYI only." />
        <PriorityRow color="gray" label="P4: <6" description="Not an anomaly." />
      </div>
    </section>
  );
};

const PRIORITY_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  red: { border: 'border-l-red-500', bg: 'bg-red-950/40', text: 'text-red-200' },
  yellow: { border: 'border-l-yellow-500', bg: 'bg-yellow-950/40', text: 'text-yellow-200' },
  blue: { border: 'border-l-blue-500', bg: 'bg-blue-950/40', text: 'text-blue-200' },
  green: { border: 'border-l-green-500', bg: 'bg-green-950/40', text: 'text-green-200' },
  gray: { border: 'border-l-gray-500', bg: 'bg-bg-secondary', text: 'text-fg-secondary' },
};

interface IPriorityRowProps {
  color: keyof typeof PRIORITY_COLORS;
  label: string;
  description: string;
}

const PriorityRow = ({ color, label, description }: IPriorityRowProps) => {
  const tone = PRIORITY_COLORS[color];
  return (
    <div className={`border-l-4 ${tone.border} ${tone.bg} px-3 py-2`}>
      <div className={`text-sm font-semibold ${tone.text}`}>{label}</div>
      <div className={`text-xs ${tone.text} opacity-80`}>{description}</div>
    </div>
  );
};

export default AnomalyScoreRanges;
